<template>
	<div class="content-page content-page--user-history">
		<div class="content-page__heading">
			<div class="content-page__breadcrumb-title">
				<Breadcrumbs :items="urlNames" />
				<h2 class="content-page__title">
					User History
					<span class="content-page__title--detail" v-if="pageTitle"
						>- {{ pageTitle }}</span
					>
				</h2>
			</div>
			<Button
				v-if="resultCounter"
				className="btn--has-icon"
				labelText="Export"
				:btnClick="downloadOnCSV"
				:iconClassName="'icon icon-download'"
				:iconBeforeLabel="true"
			></Button>
		</div>
		<div class="results-toolbar">
			<HistoryFilters
				v-model="filters"
				:defaultFilters="defaultFilters"
				:userOptions="users || []"
				placeholder="Search by settings change"
				:ddUserWidth="'185px'"
				:searchWidth="310"
				:datePickerWidth="185"
			/>

			<div class="results-toolbar__counter" v-show="resultCounter > 0">
				<!--DEV NOTE: Component is pending development's implemention.-->
				<ResultCounter :counter="resultCounter" />
			</div>
		</div>
		<div class="content-page__modifyby" v-if="showModifiedByLabel">
			<IconInfo
				class="content-page__modifyby-info"
				:position="'top'"
				:fontSize="'16px'"
				:text="'Last synched by DevOps'"
			/>
			<div class="content-page__modifyby-text">
				{{ showModifiedByLabel }}
			</div>
		</div>

		<!--DEV NOTE: Component is pending development's implemention. Mobile table Variation 1, no select.
        The table column titles should be Date/Time | Action | Changed By -->
		<ScrollContainer
			v-if="hasResults && !loading"
			:class="{ 'is-disabled': downloadCSV }"
			data-test="pageContent"
		>
			<Table
				class="table-container--user-history"
				:sourceObject="tableSource"
				v-if="hasResults"
				@change="updatedPagination"
				@sort="updateDirection"
				:isArrowUp="true"
				:sortOptions="handleSort"
			>
				<template #col-group>
					<colgroup>
						<!-- Date/Time -->
						<col class="table__col table__col--date" />

						<!-- Action -->
						<col class="table__col table__col--action" />

						<!-- Changed by -->
						<col class="table__col table__col--changed-by" />
					</colgroup>
				</template>

				<template #body-timestamp="{ body }">
					<div class="user-history-data">
						<div class="user-history-data__date-time">
							{{ body.timestamp }}
						</div>
						<div class="user-history-data__text-emphasis">
							<strong>{{ body.differenceDateString }}</strong>
						</div>
					</div>
				</template>
				<template #body-action="{ body , index}">
					<div
						class="user-history-data__text-action"
						ref="actionWidthRef"
					>
						<div
							class="user-history-data__action-description"
							v-html="body.actionDescription"
						></div>
						<div
							class="user-history-data__action-detail-button"
							v-if="body.hasTruncate"
						>
							<a
								class="link"
								v-on:click.prevent="
									() => displayHistoryDetailModal(index)
								"
								>{{ body.totalChangeDescription }}
							</a>
						</div>
					</div>
				</template>
			</Table>
		</ScrollContainer>
		<UserHistoryDetailModal
			@close="closeHistoryDetailModal"
			:logs="fieldChanges"
			v-if="showHistoryDetailModal"
		/>
		<Spinner :processor="{ loading: loading || downloadCSV }" />

		<NoResults v-if="!hasResults && !loading" />
	</div>
</template>

<script>
import Button from '@/components/forms/button/button';
import ResultCounter from '@/site-components/result-counter/result-counter';
import Table from '@/components/shells/table/table';
import Breadcrumbs from '@/components/shells/breadcrumbs/breadcrumbs';
import Spinner from '@/components/visual/spinner/spinner';
import NoResults from '@/site-components/no-results/no-results';
import { mapGetters, mapState, mapActions } from 'vuex';
import headerOptions from './header-options';
import HistoryFilters from '@/pages/global-history-screen-filters';
import IconInfo from '@/components/notifications/icon-info/icon-info';
import UserHistoryDetailModal from '@/sections/users/history/user-history-detail-modal.vue';
import ScrollContainer from '@/components/shells/scroll-container/scroll-container';
import {
	MODULE_NAME,
	USER_HISTORY_LOGS,
	SET_USER_HISTORY_LOGS
} from '@/sections/users/users-mutation-types';
import UserLogs from '@/sections/users/mixins/user-logs';
import { formatDate } from '@/utils/datetime';
import { sub } from 'date-fns';
import { parseDateToUTC } from '@/utils/date/parseDate';
import _ from 'lodash';
import {
	removeObjectEmptyValues,
	removePropertiesWithSameValues
} from '@/utils/object';

export default {
	name: 'UserHistory',
	mixins: [UserLogs],
	components: {
		Button,
		ResultCounter,
		HistoryFilters,
		Table,
		Breadcrumbs,
		Spinner,
		NoResults,
		IconInfo,
		UserHistoryDetailModal,
		ScrollContainer
	},
	props: {
		orderBy: String,
		orderDirection: String,
		search: String,
		minDatetime: String,
		maxDatetime: String,
		changedByUserId: String,
		SourceApplication: String,
		changedEntityId: [String, Number],
		recordStart: Number,
		entityType: {
			type: [String, Number]
		},
		maxCount: Number
	},
	data() {
		return {
			loading: false,
			filters: {},
			tableSource: {
				headers: [],
				displayHeader: true,
				source: {}
			},
			defaultFilters: {
				orderBy: 'timestamp',
				orderDirection: 'desc',
				search: undefined,
				minDatetime: formatDate(
					new Date(sub(new Date(), { weeks: 1 }))
				),
				maxDatetime: formatDate(new Date()),
				changedByUserId: undefined,
				SourceApplication: ['VueSupport'],
				changedEntityId: this.changedEntityId,
				recordStart: 1,
				maxCount: 10
			},
			defaultUrlNames: [
				{
					url: `/users`,
					name: 'Users'
				},
				{
					name: 'History'
				}
			],
			downloadCSV: false,
			showHistoryDetailModal: false,
			actionClientWidth: 0,
			indexLogSeleted: 0,
			fieldChanges: {},
			windowWidth: window.innerWidth
		};
	},
	async created() {
		this.tableSource.headers = headerOptions;
		this.loadUsers();
	},
	computed: {
		...mapGetters('roles', ['roles']),
		...mapGetters(MODULE_NAME, [USER_HISTORY_LOGS]),
		...mapState('historyLogs', ['users']),

		resultCounter() {
			return this.tableSource.source.totalCount;
		},
		hasResults() {
			return this.resultCounter > 0;
		},
		handleSort() {
			return {
				currentSort: this.filters.orderBy,
				currentSortDir: this.filters.orderDirection
			};
		},
		pageTitle() {
			return this[USER_HISTORY_LOGS].pageTitle || '';
		},
		urlNames() {
			const routeBreadcrumb = this.$route.meta.breadcrumb;
			const storeBreadcrumb = this.$store.getters.breadcrumbs(
				this.$route.path
			);

			if (!_.isEmpty(routeBreadcrumb)) {
				return routeBreadcrumb;
			}

			if (!_.isEmpty(storeBreadcrumb)) {
				return storeBreadcrumb;
			}

			return this.defaultUrlNames;
		},
		showModifiedByLabel() {
			const { modifiedByUsername, modifiedDate } = this[
				USER_HISTORY_LOGS
			];

			if (!modifiedDate || !modifiedDate) {
				return '';
			}
			const formattedDate = this.shortDateString(modifiedDate);
			const formattedTime = this.timeAndTimeZone(modifiedDate);

			return `User updated and synched in All Client Databases by ${modifiedByUsername} on ${formattedDate} at ${formattedTime}`;
		}
	},
	methods: {
		...mapActions(MODULE_NAME, { fetchUserLogs: SET_USER_HISTORY_LOGS }),
		...mapActions('historyLogs', ['getUsers']),
		async loadUsers() {
			const usersParams = {
				entityType: 'User',
				changedEntityId: this.changedEntityId
			};
			await this.getUsers(usersParams).catch(() => {});
		},
		shouldLoadUserApi() {
			const usersApi = this.users.map(user => user.value);
			const userLogs = [
				...new Set(
					this[USER_HISTORY_LOGS].items.map(x => x.changedByUserId)
				)
			];

			const isDiff =
				usersApi.length === userLogs.length &&
				usersApi.every(id => userLogs.includes(id));
			return !isDiff;
		},
		async fetchDataLogs() {
			if (!this.downloadCSV) {
				this.loading = true;
			}

			let filteredQuery = removeObjectEmptyValues(this.filters);
			filteredQuery.entityType = ['User'];

			if (this.downloadCSV) {
				filteredQuery.recordStart = 1;
				filteredQuery.maxCount = 100000;
			}
			filteredQuery.minDatetime = parseDateToUTC(
				filteredQuery.minDatetime
			);
			filteredQuery.maxDatetime = parseDateToUTC(
				filteredQuery.maxDatetime,
				true
			);

			if (filteredQuery.search) {
				const searchMap = {
					added: 'add',
					create: 'add',
					created: 'add',
					remove: 'delete',
					removed: 'delete',
					deleted: 'delete'
				};

				const lowerCaseSearch = filteredQuery.search.toLowerCase();
				filteredQuery.search =
					searchMap[lowerCaseSearch] || filteredQuery.search;
			}

			try {
				await this.fetchUserLogs(filteredQuery).then(async () => {
					if (this.shouldLoadUserApi()) {
						this.loadUsers();
					}
				});

				const userHistoryLogs = this.filterUserHistoryLogsByCriteria();

				if (this.downloadCSV) {
					this.exportCSV(userHistoryLogs);
				} else {
					this.tableSource.source = this.parseUsersHistoryLogs(
						userHistoryLogs,
						false
					);
				}
			} finally {
				if (this.loading) {
					this.loading = false;
				}
			}
		},
		async downloadOnCSV() {
			this.downloadCSV = true;
			await this.fetchDataLogs();
			this.downloadCSV = false;
		},
		displayHistoryDetailModal(index) {
			this.indexLogSeleted = index;
			const field = this.tableSource.source.items[this.indexLogSeleted];
			const keysToSelected = [
				'action',
				'changedByUserName',
				'fieldChanges',
				'timestamp'
			];
			this.fieldChanges = _.pick(field, keysToSelected);
			this.showHistoryDetailModal = true;
		},
		closeHistoryDetailModal(value) {
			this.showHistoryDetailModal = value;
			this.indexLogSeleted = 0;
		},
		updateRoute() {
			let queries = removePropertiesWithSameValues(
				this.filters,
				this.defaultFilters
			);

			queries = removeObjectEmptyValues(queries);

			this.$router
				.push({
					path: `/users/${this.changedEntityId}/history`,
					query: queries
				})
				.catch(() => {});
		},
		updateDirection(sortValue) {
			this.filters.orderDirection = sortValue.sortDir;
			this.filters.orderBy = sortValue.sortBy;
		},
		updatedPagination(value) {
			this.filters.recordStart = value.page;
			this.filters.maxCount = value.countPerPage;
		}
	},
	watch: {
		'filters.changedByUserId': {
			immediate: true,
			handler(val) {
				if (val) {
					this.filters.recordStart = 1;
				}
			}
		},
		filters: {
			deep: true,
			handler(newValue) {
				this.updateRoute(newValue);
			}
		},

		'$route.query': {
			immediate: true,
			handler(newQuery) {
				if (newQuery.maxCount) {
					const availablemaxCount = [10, 20, 30, 50, 100];

					if (availablemaxCount.includes(this.maxCount)) {
						newQuery.maxCount = this.maxCount;
					} else {
						newQuery.maxCount = this.defaultFilters.maxCount;
					}
				}

				this.filters = { ...this.defaultFilters, ...newQuery };
				this.fetchDataLogs();
			}
		}
	}
};
</script>

<style lang="scss" scoped>
.user-history-data {
	display: flex;
	flex-direction: column;
	gap: $gutter-sm;

	&__date-time {
		display: flex;
		flex-wrap: wrap;
		column-gap: $gutter-lg;
		row-gap: $gutter-sm;
		justify-content: space-between;
		white-space: pre;
	}
	&__text-emphasis,
	&__text-action {
		display: flex;
		justify-content: space-between;
	}
	&__action-description {
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		text-transform: lowercase;
	}
	&__action-detail-button {
		text-transform: none;
		text-align: right;
		font-size: $bd-txt-font-size-sm;
		text-wrap: nowrap;
	}
}
.table-container--user-history {
	::v-deep .table {
		table-layout: fixed;
	}
	.table__col {
		width: 100%;

		@include mq($from: md) {
			&--date {
				width: 200px;
			}
			&--action {
				width: auto;
			}
			&--changed-by {
				width: 165px;
			}
		}

		@include mq($from: lg) {
			&--date {
				width: 200px;
			}
			&--action {
				width: auto;
			}
			&--changed-by {
				width: 265px;
			}
		}
	}
}
.results-toolbar {
	width: 100%;
}
.content-page {
	&__modifyby {
		display: flex;
		gap: $gutter-md;
		padding-bottom: $gutter-lg;
		&-text {
			font-size: $bd-txt-font-size-regular;
		}
	}
}
</style>

<style lang="scss">
strong {
	@include body-text('regular-emphasis');
	text-transform: capitalize;
	font-size: $bd-txt-font-size-regular;
}
.user-history-data {
	&__action-description,
	&__text-emphasis {
		strong {
			font-weight: 600;
		}
	}
}
</style>
